import { useCallback, useMemo } from 'react';

import { CommunicationType, SenderType, useCreateApp } from '../../../modules/Apps';
import { getApiErrorMessage } from '../../../utils/Api';
import { PopoverStateReturn } from '../../common/Drawer/Drawer.context';
import { FormDrawer } from '../../common/FormDrawer';
import { useToasterContext } from '../../common/Toaster';
import { useAppsTableContext } from '../AppsTable/AppsTableContext';

import { useCreateNewTwilioAccount } from './lib';

import {
  AppSettingsForm,
  AppSettingsFormValues,
  AppSettingsFormValuesToDto,
  createAppSettingsFormValidationSchema,
  TwilioAccountOption,
} from '.';

interface CreateAppSettingsDrawerProps {
  state: PopoverStateReturn;
}

export function CreateAppSettingsDrawer({ state }: CreateAppSettingsDrawerProps) {
  const { mutateAsync } = useCreateApp();
  const createNewTwilioAccount = useCreateNewTwilioAccount();

  const { setAppSecrets } = useAppsTableContext();

  const { showSuccess, showError } = useToasterContext();

  const handleSubmit = useCallback(
    async (values: AppSettingsFormValues) => {
      if (values.twilioAccountOption === TwilioAccountOption.New) {
        values.twilioAccountId = await createNewTwilioAccount(values);
      }
      await mutateAsync(AppSettingsFormValuesToDto(values), {
        onSuccess: ({ apiPrimaryKey, apiSecondaryKey }) => {
          showSuccess('App successfully created.');
          setAppSecrets({ apiPrimaryKey, apiSecondaryKey });
        },
        onError: error =>
          showError(
            getApiErrorMessage(
              error,
              'There was an error creating the App. Please, try again later.',
            ),
          ),
      });
    },
    [createNewTwilioAccount, mutateAsync, setAppSecrets, showError, showSuccess],
  );

  const initialValues: AppSettingsFormValues = useMemo(
    () => ({
      code: '',
      defaultCountryCode: 'US',
      defaultEmailSender: '',
      defaultSMSSender: '',
      defaultVoiceNumber: '',
      name: '',
      twilioAccountId: '',
      twilioAccountSid: '',
      twilioAccountAuthToken: '',
      helpMessage: '',
      optInMessage: '',
      optOutMessage: '',
      startMessage: '',
      twilioAccountOption: TwilioAccountOption.Existing,
      communicationType: CommunicationType.Both,
      sendgridPromotionalAsmGroupId: 0,
      sendgridTransactionalAsmGroupId: 0,
    }),
    [],
  );

  const validationSchema = useMemo(() => createAppSettingsFormValidationSchema(), []);

  return (
    <FormDrawer
      state={state}
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      title="Add Application"
      subtitle="Create a new OIM application"
      validationSchema={validationSchema}
      validateOnBlur={false}
    >
      {() => <AppSettingsForm />}
    </FormDrawer>
  );
}
